// 확정 세금계산서 → 출력물 표시 문서.
//
// 서버(GET /tax-invoices/{taxNo}/document)의 확정값을 옮기기만 한다.

import type {
  BuyerInfo,
  SupplierInfo,
  TaxInvoiceDocument,
  TaxItemView,
} from '@/lib/api/data-contracts';
import { formatBizNo } from '@/components/features/sales/invoice/invoice-build-doc';
import type {
  TaxInvoiceDocumentDto,
  TaxInvoiceItemRow,
  TaxInvoiceParty,
} from './tax-invoice-document-types';

/** 영수(01) / 청구(02) */
const RECEIPT_PURPOSE_RECEIPT = '01';

export function buildTaxInvoiceDocument(
  document: TaxInvoiceDocument
): TaxInvoiceDocumentDto {
  const issueDate = document.issueDate ?? '';
  return {
    taxNo: document.taxNo ?? 0,
    titleText: '전자세금계산서',
    approvalNo: document.hometaxTaxNo ?? '',
    issueDate,
    supplyAmt: document.supplyAmtTotal ?? 0,
    vatAmt: document.vatAmtTotal ?? 0,
    totalAmt: document.totalAmt ?? 0,
    modifyReason: '해당없음',
    remark: document.remark ?? '',
    supplier: toSupplierParty(document.supplier),
    customer: toBuyerParty(document.buyer),
    items: (document.items ?? []).map(item => toItemRow(item, issueDate)),
    cash: document.cashAmt,
    checkAmt: document.checkAmt,
    noteAmt: document.noteAmt,
    creditAmt: document.creditAmt,
    receiptType:
      document.receiptPurposeCode === RECEIPT_PURPOSE_RECEIPT ? '영수' : '청구',
  };
}

/** 품목 일자는 일 2자리만 저장되므로 월은 작성일자에서 가져온다 */
function toItemRow(item: TaxItemView, issueDate: string): TaxInvoiceItemRow {
  return {
    month: issueDate.slice(5, 7),
    day: item.itemDate ?? '',
    itemName: item.itemName ?? '',
    spec: item.spec ?? '',
    qty: item.qty ?? null,
    unitPrice: item.unitPrice ?? null,
    supplyAmt: item.supplyAmt ?? 0,
    vatAmt: item.vatAmt ?? 0,
    remark: '',
  };
}

function toSupplierParty(supplier: SupplierInfo | undefined): TaxInvoiceParty {
  return {
    bizNo: formatBizNo(supplier?.entCode ?? ''),
    name: supplier?.name ?? '',
    ceo: supplier?.rep ?? '',
    address: supplier?.addr ?? '',
    subBizNo: supplier?.subBizNo,
    bizType: supplier?.bizType,
    bizItem: supplier?.bizItem,
    email: supplier?.email,
  };
}

/** 미등록문서(orphan)는 사업자번호가 어느 거래처와도 맞지 않아 공급받는자가 없을 수 있다 */
function toBuyerParty(buyer: BuyerInfo | undefined): TaxInvoiceParty {
  return {
    bizNo: formatBizNo(buyer?.entCode ?? ''),
    name: buyer?.name ?? '',
    ceo: buyer?.rep ?? '',
    address: buyer?.addr ?? '',
    bizType: buyer?.entType,
    bizItem: buyer?.entStyle,
    email: buyer?.email,
  };
}
